<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="relative z-50" @close="$emit('cancel')">
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-900/70 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel class="relative w-full max-w-xl transform overflow-hidden rounded-lg bg-white px-8 pt-8 pb-8 text-left shadow-xl transition-all sm:my-8">
                            <DialogTitle as="h3" class="text-2xl font-bold text-gray-900">
                                Property Based Subscription
                            </DialogTitle>

                            <p class="mt-5 text-sm leading-6 text-gray-700">
                                Activating this property will subscribe your first property, plus each additional
                                property at 50% of your original subscription amount. You will only be billed for
                                active and paused properties in your dashboard. Archived properties do not count
                                towards your subscription.
                            </p>

                            <label
                                v-if="requiresAcknowledgement"
                                class="mt-6 flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-gray-800"
                            >
                                <input
                                    v-model="acknowledged"
                                    type="checkbox"
                                    class="mt-0.5 h-4 w-4 rounded border-gray-300 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400"
                                />
                                <span>{{ acknowledgementText }}</span>
                            </label>

                            <div class="mt-8 flex justify-end gap-3">
                                <button
                                    type="button"
                                    class="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                    @click="$emit('cancel')"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    class="rounded-md bg-rent-risk-blue-400 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                                    :disabled="!canConfirm || processing"
                                    @click="$emit('confirm')"
                                >
                                    {{ processing ? 'Activating...' : 'Activate property' }}
                                </button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'

const props = defineProps({
    show: { type: Boolean, default: false },
    processing: { type: Boolean, default: false },
    requiresMultiPropertyAck: { type: Boolean, default: true },
    requiresPerPropertyAck: { type: Boolean, default: false },
    perPropertyPriceFormatted: { type: String, default: null },
})

defineEmits(['confirm', 'cancel'])

const acknowledged = ref(false)

const requiresAcknowledgement = computed(() => {
    return props.requiresMultiPropertyAck || props.requiresPerPropertyAck
})

const perPropertyPriceLabel = computed(() => {
    return props.perPropertyPriceFormatted ?? '$0.00'
})

const acknowledgementText = computed(() => {
    if (props.requiresMultiPropertyAck && props.requiresPerPropertyAck) {
        return `I understand that I will be billed once I activate two or more properties and that activating this property will increase my subscription by ${perPropertyPriceLabel.value}/month/property.`
    }

    if (props.requiresMultiPropertyAck) {
        return 'I understand that I will be billed once I activate two or more properties and that all my active and paused properties in my dashboard will count toward my subscription.'
    }

    if (props.requiresPerPropertyAck) {
        return `I understand that activating this property will increase my subscription by ${perPropertyPriceLabel.value}/month/property.`
    }

    return ''
})

const canConfirm = computed(() => {
    if (!requiresAcknowledgement.value) {
        return true
    }

    return acknowledged.value
})

watch(() => props.show, (visible) => {
    if (!visible) {
        acknowledged.value = false
    }
})
</script>
